import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUser,
  getUserReceipts,
  getUserFailedPurchases,
  getUserFullDramaPurchases,
  grantFreeCoin,
} from "@/services/firebase/user";
import { usePermissions } from "@/hooks/usePermissions";
import { Receipt } from "@/types/receipt";
import { FailedPurchase } from "@/types/failedPurchase";
import { formatDateTime, formatDate } from "@/utils/dateFormat";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminDataTable,
  AdminEmptyState,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Gift, Loader2 } from "lucide-react";

type TabType = "overview" | "receipts" | "failed" | "usage";

const receiptTypeLabels: Record<string, string> = {
  purchase: "購買金幣",
  unlock: "解鎖影片",
  full_drama_coin_purchase: "全劇購買（金幣）",
  gift_code: "兌換禮物代碼",
  daily_checkin: "每日簽到",
  mission_reward: "任務獎勵",
  app_usage_reward: "App 使用獎勵",
  ad_reward: "觀看廣告獎勵",
  admin_grant: "後台發放",
  refund: "退款",
  adjustment: "系統調整",
};

const failureStageLabels: Record<string, string> = {
  validation: "驗證階段",
  verification: "確認階段",
  database: "資料庫階段",
  unknown: "未知",
};

const statusLabels: Record<string, string> = {
  pending: "待處理",
  investigating: "調查中",
  resolved: "已解決",
  rejected: "已拒絕",
};

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [imageError, setImageError] = useState(false);

  // Grant Free Coin dialog state
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [grantAmount, setGrantAmount] = useState("100");
  const [grantReason, setGrantReason] = useState("");

  const { data: user, isLoading, error } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUser(id!),
    enabled: !!id,
  });

  const grantMutation = useMutation({
    mutationFn: () => {
      const amt = parseInt(grantAmount, 10);
      if (!Number.isFinite(amt) || amt <= 0) {
        throw new Error("金幣數量必須為正整數");
      }
      if (grantReason.trim().length < 2) {
        throw new Error("原因必填（至少 2 個字）");
      }
      return grantFreeCoin(id!, amt, grantReason.trim());
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      queryClient.invalidateQueries({ queryKey: ["user-receipts", id] });
      setShowGrantDialog(false);
      setGrantAmount("100");
      setGrantReason("");
      alert(
        `✅ 發放成功\n` +
          `新增 Free coin 數量：${data.transaction_id ? grantAmount : 0}\n` +
          `目前餘額：${data.new_total.toLocaleString()} 金幣（paid ${data.new_paid.toLocaleString()} + free ${data.new_free.toLocaleString()}）`
      );
    },
    onError: (err: Error) => {
      alert(`發放失敗：${err.message}`);
    },
  });

  // 當用戶資料更新時，重置圖片錯誤狀態
  React.useEffect(() => {
    if (user) {
      setImageError(false);
    }
  }, [user?.photoURL]);

  // 基本資訊和總覽所需的資料立即載入
  const { data: receipts, isLoading: receiptsLoading } = useQuery({
    queryKey: ["user-receipts", id],
    queryFn: () => getUserReceipts(id!),
    enabled: !!id,
  });

  const { data: failedPurchases, isLoading: failedLoading } = useQuery({
    queryKey: ["user-failed-purchases", id],
    queryFn: () => getUserFailedPurchases(id!),
    enabled: !!id,
  });

  // 全劇購買記錄資料按需載入（只在切換到 usage tab 時載入）
  const { data: fullDramaPurchases, isLoading: fullDramaLoading, error: fullDramaError } = useQuery({
    queryKey: ["user-full-drama-purchases", id],
    queryFn: () => {
      console.log("[UserDetailPage] 開始查詢全劇購買記錄，userId:", id);
      return getUserFullDramaPurchases(id!);
    },
    enabled: !!id && activeTab === "usage",
  });

  // 計算統計資訊（使用 useMemo 優化性能）
  const { totalSpent, totalEarned } = useMemo(() => {
    const spent = (receipts || [])
      .filter((r) => r.amount < 0)
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);
    const earned = (receipts || [])
      .filter((r) => r.amount > 0)
      .reduce((sum, r) => sum + r.amount, 0);
    return { totalSpent: spent, totalEarned: earned };
  }, [receipts]);

  const tabs: Array<{ id: TabType; label: string }> = useMemo(() => [
    { id: "overview", label: "總覽" },
    {
      id: "receipts",
      label: `購買紀錄 (${receiptsLoading ? "..." : receipts?.length || 0})`,
    },
    {
      id: "failed",
      label: `購買失敗 (${failedLoading ? "..." : failedPurchases?.length || 0})`,
    },
    { id: "usage", label: "全劇購買記錄" },
  ], [receipts?.length, failedPurchases?.length, receiptsLoading, failedLoading]);

  // Receipt table columns
  const receiptColumns: AdminTableColumn[] = [
    { key: "type", label: "類型" },
    { key: "amount", label: "金額", sortable: true },
    { key: "drama", label: "劇集/影片", hideBelow: "md" },
    { key: "platform", label: "平台", hideBelow: "md" },
    { key: "created_at", label: "時間", sortable: true },
  ];

  // Failed purchase table columns
  const failedColumns: AdminTableColumn[] = [
    { key: "transaction_id", label: "交易 ID" },
    { key: "type", label: "類型", hideBelow: "md" },
    { key: "platform", label: "平台", hideBelow: "md" },
    { key: "error_code", label: "錯誤碼", hideBelow: "md" },
    { key: "failure_stage", label: "失敗階段", hideBelow: "lg" },
    { key: "status", label: "狀態" },
    { key: "timestamp", label: "時間", hideBelow: "md" },
  ];

  if (isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  if (error || !user) {
    return (
      <AdminPage>
        <AdminErrorAlert
          message={error instanceof Error ? error.message : "找不到此使用者"}
        />
        <Button variant="outline" onClick={() => navigate("/users")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          返回列表
        </Button>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="使用者詳情"
        subtitle={`ID: ${user.id}`}
        actions={
          <Button variant="outline" onClick={() => navigate("/users")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回列表
          </Button>
        }
      />

      {/* 基本資訊卡片 */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {user.photoURL && !imageError ? (
              <img
                src={user.photoURL}
                alt={user.displayName || "User"}
                className="w-20 h-20 rounded-full"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-2xl">
                {user.displayName?.[0] || user.email?.[0] || "U"}
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {user.displayName || "未設定名稱"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <span className="ml-2 text-foreground">
                    {user.email || "未設定"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">註冊時間:</span>
                  <span className="ml-2 text-foreground">
                    {formatDateTime(user.created_at || user.createdAt)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">最後登入:</span>
                  <span className="ml-2 text-foreground">
                    {user.last_login || (user as any).lastLoginAt
                      ? formatDateTime(user.last_login || (user as any).lastLoginAt)
                      : "從未登入"}
                  </span>
                </div>
                {user.phoneNumber && (
                  <div>
                    <span className="text-muted-foreground">電話:</span>
                    <span className="ml-2 text-foreground">
                      {user.phoneNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 金幣與會員資訊 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="relative">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="absolute top-4 right-4"
              onClick={() => setShowGrantDialog(true)}
              title="後台發放免費金幣"
            >
              <Gift className="h-3.5 w-3.5 mr-1" />
              發放金幣
            </Button>
          )}
          <CardContent className="p-6">
            <div className="text-sm text-muted-foreground mb-1">當前金幣</div>
            <div className="text-2xl font-bold text-foreground">
              {user.coin?.toLocaleString() || 0}
            </div>
            {(user.paid_coin !== undefined || user.free_coin !== undefined) && (
              <div className="text-xs text-muted-foreground mt-2 space-x-3">
                <span>付費 {(user.paid_coin ?? 0).toLocaleString()}</span>
                <span>/</span>
                <span>免費 {(user.free_coin ?? 0).toLocaleString()}</span>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-muted-foreground mb-1">會員狀態</div>
            <div className="text-2xl font-bold">
              {user.is_member ? (
                <span className="text-purple-500">
                  {user.member_type === "yearly" ? "年會員" : "週會員"}
                </span>
              ) : (
                <span className="text-muted-foreground">一般用戶</span>
              )}
            </div>
            {user.is_member && user.member_until && (
              <div className="text-xs text-muted-foreground mt-1">
                到期: {formatDate(user.member_until)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tab 導航 */}
      <Card>
        <div className="border-b">
          <div className="flex gap-2 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* 總覽 Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* 統計資訊 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-sm text-muted-foreground mb-1">總消費金額</div>
                  <div className="text-xl font-semibold text-foreground">
                    {totalSpent.toLocaleString()} 金幣
                  </div>
                </div>
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-sm text-muted-foreground mb-1">總獲得金額</div>
                  <div className="text-xl font-semibold text-foreground">
                    {totalEarned.toLocaleString()} 金幣
                  </div>
                </div>
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-sm text-muted-foreground mb-1">交易記錄數</div>
                  <div className="text-xl font-semibold text-foreground">
                    {receipts?.length || 0} 筆
                  </div>
                </div>
              </div>

              {/* 最近交易 */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  最近交易
                </h3>
                {receiptsLoading ? (
                  <AdminLoadingSkeleton rows={3} />
                ) : receipts && receipts.length > 0 ? (
                  <div className="space-y-2">
                    {receipts.slice(0, 5).map((receipt) => (
                      <div
                        key={receipt.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {receiptTypeLabels[receipt.type] || receipt.type}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatDateTime(receipt.created_at)}
                          </div>
                        </div>
                        <div
                          className={`text-sm font-semibold ${
                            receipt.amount >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {receipt.amount >= 0 ? "+" : ""}
                          {receipt.amount.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <AdminEmptyState title="尚無交易記錄" />
                )}
              </div>
            </div>
          )}

          {/* 購買紀錄 Tab */}
          {activeTab === "receipts" && (
            <AdminDataTable
              columns={receiptColumns}
              data={receipts || []}
              loading={receiptsLoading}
              emptyTitle="尚無交易記錄"
              pageSize={20}
              getRowId={(item) => item.id}
              renderCell={(receipt, col) => {
                switch (col.key) {
                  case "type":
                    return (
                      <span className="text-sm text-foreground">
                        {receiptTypeLabels[receipt.type] || receipt.type}
                      </span>
                    );
                  case "amount":
                    return (
                      <span
                        className={`text-sm font-semibold ${
                          receipt.amount >= 0
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {receipt.amount >= 0 ? "+" : ""}
                        {receipt.amount.toLocaleString()}
                      </span>
                    );
                  case "drama":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {receipt.drama_title || receipt.drama_id || "-"}
                        {receipt.episode && ` 第${receipt.episode}集`}
                      </span>
                    );
                  case "platform":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {receipt.platform || "-"}
                      </span>
                    );
                  case "created_at":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(receipt.created_at)}
                      </span>
                    );
                  default:
                    return null;
                }
              }}
            />
          )}

          {/* 購買失敗紀錄 Tab */}
          {activeTab === "failed" && (
            <AdminDataTable
              columns={failedColumns}
              data={failedPurchases || []}
              loading={failedLoading}
              emptyTitle="尚無購買失敗記錄"
              pageSize={20}
              getRowId={(item) => item.id}
              renderCell={(failed, col) => {
                switch (col.key) {
                  case "transaction_id":
                    return (
                      <span className="text-sm font-mono text-foreground">
                        {failed.transaction_id.substring(0, 12)}...
                      </span>
                    );
                  case "type":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {failed.type === "coin_purchase" ? "購買金幣" : "全劇購買"}
                      </span>
                    );
                  case "platform":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {failed.platform === "ios" ? "iOS" : "Android"}
                      </span>
                    );
                  case "error_code":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {failed.error_code}
                      </span>
                    );
                  case "failure_stage":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {failureStageLabels[failed.failure_stage] || failed.failure_stage}
                      </span>
                    );
                  case "status":
                    return (
                      <Badge
                        variant={
                          failed.status === "resolved"
                            ? "default"
                            : failed.status === "rejected"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {statusLabels[failed.status || "pending"]}
                      </Badge>
                    );
                  case "timestamp":
                    return (
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(failed.timestamp)}
                      </span>
                    );
                  default:
                    return null;
                }
              }}
            />
          )}

          {/* 全劇購買記錄 Tab */}
          {activeTab === "usage" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  全劇購買記錄
                </h3>
                {fullDramaLoading ? (
                  <AdminLoadingSkeleton rows={3} />
                ) : fullDramaError ? (
                  <AdminErrorAlert
                    message={fullDramaError instanceof Error ? fullDramaError.message : "載入失敗"}
                  />
                ) : fullDramaPurchases && fullDramaPurchases.length > 0 ? (
                  <div className="space-y-2">
                    {fullDramaPurchases.map((purchase) => (
                      <div
                        key={purchase.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">
                            {purchase.drama_title || purchase.drama_id}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            劇集 ID: {purchase.drama_id}
                            {purchase.total_episodes && ` | 總集數: ${purchase.total_episodes}`}
                            {purchase.platform && ` | 平台: ${purchase.platform.toUpperCase()}`}
                          </div>
                          {purchase.transaction_id && (
                            <div className="text-xs text-muted-foreground">
                              交易 ID: {purchase.transaction_id}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground ml-4">
                          {formatDateTime(purchase.purchased_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <AdminEmptyState title="尚無全劇購買記錄" />
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* 發放金幣對話框 */}
      {showGrantDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-[460px] shadow-xl">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Gift className="h-4 w-4" />
                後台發放免費金幣
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                發放給用戶 <span className="font-medium text-foreground">{user.displayName || user.email || user.id}</span> 免費金幣。
                系統將完整記錄發放者、時間與原因。
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    金幣數量（正整數，上限 100000）
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100000"
                    step="1"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    發放原因（必填）
                  </label>
                  <textarea
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                    placeholder="例：客服補償、活動獎勵、App bug 補發..."
                  />
                </div>
              </div>
              <div className="mt-4 p-3 bg-muted rounded text-xs text-muted-foreground">
                💡 發放後會寫入：<br />
                • 用戶 receipt（用戶可在 App 查到）<br />
                • 全平台 transactions（可在 CMS 交易記錄頁查詢）<br />
                • type = <code>admin_grant</code>，描述含發放者 email
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <Button
                  variant="ghost"
                  onClick={() => setShowGrantDialog(false)}
                  disabled={grantMutation.isPending}
                >
                  取消
                </Button>
                <Button
                  onClick={() => grantMutation.mutate()}
                  disabled={grantMutation.isPending}
                >
                  {grantMutation.isPending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      發放中...
                    </>
                  ) : (
                    "確認發放"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminPage>
  );
}
